/**
 * KV abstraction for anything that must survive an app restart.
 *
 * Web → `localStorage`: synchronous read, synchronous write, no hydrate dance.
 * Native (Capacitor) → `@aparajita/capacitor-secure-storage` (iOS Keychain,
 * Android Keystore). The plugin API is async-only, so we keep an in-memory
 * cache that is filled once on boot; reads are then synchronous, writes update
 * the cache and fire-and-forget into the Keychain.
 *
 * **The app entry point must `await kv.hydrate()` before rendering** — otherwise
 * the first synchronous read happens before the cache is filled and a logged-in
 * user is bounced to the login screen on every cold start. Web hydrate is a no-op.
 */
import { isNative } from './platform'

export interface KVStorage {
  /** Fill the cache from the durable store. Call once on boot, before render. */
  hydrate(): Promise<void>
  /** Synchronous read. `null` when missing, unavailable, or native pre-hydration. */
  getSync(key: string): string | null
  set(key: string, value: string): void
  remove(key: string): void
}

const NOOP: KVStorage = {
  async hydrate() {},
  getSync: () => null,
  set: () => {},
  remove: () => {},
}

function makeWebKV(): KVStorage {
  return {
    async hydrate() {
      // localStorage is synchronous — nothing to hydrate.
    },
    getSync(key) {
      try {
        return window.localStorage.getItem(key)
      } catch {
        return null
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value)
      } catch {
        // quota / private mode → in-memory only
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key)
      } catch {
        // ignore
      }
    },
  }
}

function makeNativeKV(): KVStorage {
  const cache = new Map<string, string>()
  let hydrated = false

  return {
    async hydrate() {
      if (hydrated) return
      try {
        const { SecureStorage, KeychainAccess } =
          await import('@aparajita/capacitor-secure-storage')
        // No iCloud sync — a JWT is per device; syncing would leak it to another one.
        await SecureStorage.setSynchronize(false)
        // Survives reinstall, readable after the first unlock (background-fetch friendly).
        await SecureStorage.setDefaultKeychainAccess(KeychainAccess.afterFirstUnlockThisDeviceOnly)
        const keys = await SecureStorage.keys()
        await Promise.all(
          keys.map(async (key) => {
            const value = await SecureStorage.getItem(key)
            if (typeof value === 'string') cache.set(key, value)
          }),
        )
      } catch (error) {
        console.warn('[kv] SecureStorage hydrate failed', error)
      } finally {
        // Mark hydrated even on failure — the app continues with an empty cache.
        hydrated = true
      }
    },
    getSync(key) {
      return cache.get(key) ?? null
    },
    set(key, value) {
      cache.set(key, value)
      void (async () => {
        try {
          const { SecureStorage } = await import('@aparajita/capacitor-secure-storage')
          await SecureStorage.setItem(key, value)
        } catch (error) {
          console.warn('[kv] SecureStorage set failed', error)
        }
      })()
    },
    remove(key) {
      cache.delete(key)
      void (async () => {
        try {
          const { SecureStorage } = await import('@aparajita/capacitor-secure-storage')
          await SecureStorage.removeItem(key)
        } catch (error) {
          console.warn('[kv] SecureStorage remove failed', error)
        }
      })()
    },
  }
}

export const kv: KVStorage = (() => {
  if (typeof window === 'undefined') return NOOP
  return isNative() ? makeNativeKV() : makeWebKV()
})()
